import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef, useMemo } from 'react';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { App as CapacitorApp } from '@capacitor/app';
import { isNative } from '../config/platform';
import { disablePush } from '../native/push';
import { auth } from '../config/firebase';
import {
  clearToken,
  getToken,
  getTokenExpiration,
  peekToken,
  setToken as persistToken,
} from '../services/tokenStore';

// Configure axios base URL dynamically
// This ensures it uses the correct protocol (HTTPS/HTTP) based on the current page
const getApiBaseUrl = () => {
  // If REACT_APP_API_URL is set, use it (but ensure it matches current protocol if relative)
  if (process.env.REACT_APP_API_URL) {
    const envUrl = process.env.REACT_APP_API_URL;
    // If it's a full URL, use it as-is
    if (envUrl.startsWith('http://') || envUrl.startsWith('https://')) {
      return envUrl;
    }
    // If it's relative, prepend current protocol and host
    const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
    return `${protocol}//${window.location.host}${envUrl.startsWith('/') ? '' : '/'}${envUrl}`;
  }
  
  // Detect protocol from current page (HTTPS or HTTP)
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  const hostname = window.location.hostname;
  const port = window.location.port;
  
  // If served through nginx (no port or port 443/80), API is on same host
  // Otherwise, assume API is on port 8000
  if (!port || port === '443' || port === '80') {
    // API is proxied through nginx on same host
    return `${protocol}//${hostname}/api`;
  } else {
    // Development mode - API is on port 8000
    return `${protocol}//${hostname}:8000/api`;
  }
};

/**
 * Refuse to boot the native app against a non-https API.
 *
 * Under Capacitor `window.location.protocol` is 'capacitor:', so the protocol
 * ternary above falls through to 'http:', the hostname is 'localhost' and the port
 * is empty — the base URL resolves to `http://localhost/api`. iOS App Transport
 * Security blocks that outright, and an ATS block surfaces as a NETWORK error, not
 * a 401: the response interceptor below never fires, so there is no logout, no
 * redirect and no toast. The tester just watches a spinner forever with nothing in
 * the UI to explain it. A crash on launch during development is enormously cheaper
 * to diagnose than that, so make it loud.
 *
 * The iOS build supplies REACT_APP_API_URL=https://6love.app/api (see the `build:ios`
 * npm script), which is what keeps this from firing in practice.
 */
const assertSecureBaseUrl = (baseUrl: string): void => {
  if (isNative() && !baseUrl.startsWith('https://')) {
    throw new Error(
      `Refusing to start: resolved API base URL "${baseUrl}" is not https. ` +
      'The native build must be compiled with REACT_APP_API_URL=https://6love.app/api ' +
      '(see the build:ios npm script) — iOS App Transport Security silently blocks ' +
      'cleartext requests and the failure is invisible in the UI.'
    );
  }
};

// Set baseURL at module load
const initialBaseUrl = getApiBaseUrl();
assertSecureBaseUrl(initialBaseUrl);
axios.defaults.baseURL = initialBaseUrl;
console.log('Axios baseURL initialized to:', initialBaseUrl, '(REACT_APP_API_URL:', process.env.REACT_APP_API_URL || 'not set', ')');

/**
 * Bridge between the axios interceptor — which lives outside the React tree — and
 * react-router.
 *
 * This replaces `window.location.href = '/login'`. Under capacitor://localhost that
 * assignment works but costs a FULL WEBVIEW RELOAD: React unmounts, every provider
 * re-initializes, PostHog re-boots and the splash screen flashes. It is the single
 * most "this is just a website" moment in the app. The old `pathname !== '/login'`
 * guard was also unreliable there, because Capacitor's first load has pathname '/'
 * no matter which route is rendered, so the guard could not tell "on the login page"
 * from "just launched" and could double-fire.
 *
 * AuthProvider sits ABOVE <Router> in App.tsx, so navigate() has to be called from a
 * component mounted INSIDE the router. Rather than import react-router-dom here —
 * v7 ships no CJS entry that Jest can resolve, so every one of the eleven test files
 * that touch this module would need a `{ virtual: true }` mock — this exposes a
 * registration seam and lets the router-aware component live in App.tsx:
 *
 *   const AuthRedirectHandler = () => {
 *     const navigate = useNavigate();
 *     useEffect(
 *       () => registerLoginRedirect(() => navigate('/login', { replace: true })),
 *       [navigate],
 *     );
 *     return null;
 *   };
 *
 * Registering is optional: ProtectedRoute in App.tsx already renders
 * <Navigate to="/login" replace> the moment `user` goes null, so a 401 on a protected
 * page still redirects — through the router, not a reload. The seam just makes the
 * redirect immediate and covers public routes too.
 */
let navigateToLogin: (() => void) | null = null;
let loginRedirectPending = false;

/**
 * Hand the 401 interceptor a router-based way to reach the login screen.
 * Returns an unregister function, so it drops straight into a useEffect.
 */
export const registerLoginRedirect = (navigate: () => void): (() => void) => {
  navigateToLogin = navigate;
  // A 401 can land before the router has mounted; replay it now instead of
  // stranding the user on a screen whose data never arrives.
  if (loginRedirectPending) {
    loginRedirectPending = false;
    navigate();
  }
  return () => {
    if (navigateToLogin === navigate) navigateToLogin = null;
  };
};

const requestLoginRedirect = (): void => {
  if (navigateToLogin) {
    navigateToLogin();
    return;
  }
  loginRedirectPending = true;
};

interface TokenExchange {
  access_token: string;
  expires_in: number;
}

/** Re-exchange once the backend JWT has less than this much life left. */
const REFRESH_THRESHOLD_MS = 24 * 60 * 60 * 1000;

/**
 * A single shared in-flight exchange.
 *
 * The backend JWT lasts 7 days and there is NO refresh endpoint — but Firebase's own
 * ID token refreshes itself hourly, so as long as the Firebase session is alive we
 * can always mint a fresh backend JWT by replaying the existing
 * `POST /auth/firebase?firebase_token=...` exchange. Zero backend changes.
 *
 * Module-level because a single screen commonly fires several requests in parallel:
 * when the JWT lapses they all 401 at once, and without this guard they would
 * stampede /auth/firebase N times and race each other to write the token.
 */
let inFlightRefresh: Promise<TokenExchange | null> | null = null;

/** Marker on a request config so a retry (or the exchange itself) can never re-trigger a refresh. */
type RetryableConfig = AxiosRequestConfig & { _authRetry?: boolean };

/** True when Firebase still has a session we can trade for a new backend JWT. */
const canRefreshSilently = (): boolean => !!auth?.currentUser;

const refreshBackendToken = (): Promise<TokenExchange | null> => {
  if (inFlightRefresh) return inFlightRefresh;

  const exchange = (async (): Promise<TokenExchange | null> => {
    const currentUser = auth?.currentUser;
    if (!currentUser) return null;

    // force=true: the cached Firebase ID token may be the very one the backend just
    // rejected, so ask for a freshly minted one.
    const firebaseToken = await currentUser.getIdToken(true);
    const response = await axios.post('/auth/firebase', null, {
      params: { firebase_token: firebaseToken },
      _authRetry: true,
    } as RetryableConfig);
    return response.data as TokenExchange;
  })();

  inFlightRefresh = exchange.finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
};

interface User {
  id: number;
  email: string;
  name: string;
  display_name?: string;
  role: 'ADMIN' | 'PLAYER';
  google_id?: string;
  firebase_uid?: string;
  picture_url?: string;
  tennisrecord_name?: string | null;
  created_at: string;
  last_login?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loginWithFirebase: (firebaseToken: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Helper function to check if token is expired.
// Async because the token store is async — see services/tokenStore.ts.
const isTokenExpired = async (): Promise<boolean> => {
  const expirationTime = await getTokenExpiration();
  if (!expirationTime) return true;
  return Date.now() >= expirationTime;
};

/**
 * Keeps the backend JWT alive across long absences.
 *
 * Without this, a player who leaves the app closed for 8 days comes back to a silent
 * logout — the 7-day JWT lapsed and there is no refresh endpoint. Firebase's session
 * is still valid, so we just re-run the exchange when the JWT is close to the wire.
 * Two triggers, because on a phone the process often isn't restarted for weeks:
 * cold start, and every resume from the background.
 */
const useBackendTokenRefresh = (
  token: string | null,
  applyToken: (accessToken: string, expiresIn: number) => Promise<void>
) => {
  // Read the token through a ref so the callback stays stable across re-mints.
  // If it were a dependency, a successful refresh would re-run the cold-start
  // effect below with the new token — and if the backend ever issued a token
  // shorter than REFRESH_THRESHOLD_MS, that would be an unbounded loop hammering
  // /auth/firebase. Gating the effect on "is there a token at all" instead makes
  // exactly one attempt per sign-in, and resume covers the rest.
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const hasToken = token !== null;

  const refreshIfExpiringSoon = useCallback(async () => {
    if (!tokenRef.current || !canRefreshSilently()) return;

    const expiresAt = await getTokenExpiration();
    if (expiresAt === null) return;
    if (expiresAt - Date.now() > REFRESH_THRESHOLD_MS) return;

    try {
      const exchange = await refreshBackendToken();
      if (exchange) {
        await applyToken(exchange.access_token, exchange.expires_in);
      }
    } catch (error) {
      // Non-fatal: the current token is still good for up to REFRESH_THRESHOLD_MS,
      // and if it isn't, the 401 path below gets another attempt.
      console.warn('Proactive backend token refresh failed:', error);
    }
  }, [applyToken]);

  // Cold start — and re-armed whenever a session begins, since the token may only
  // arrive after an async store hydrates.
  useEffect(() => {
    void refreshIfExpiringSoon();
  }, [hasToken, refreshIfExpiringSoon]);

  // Resume from background. iOS suspends the WebView rather than killing it, so a
  // phone that sat in a pocket for a week resumes with the same JS context and the
  // cold-start check above never runs again.
  useEffect(() => {
    if (!isNative()) return;

    let cancelled = false;
    let remove: (() => void) | null = null;

    CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) void refreshIfExpiringSoon();
    })
      .then((handle) => {
        if (cancelled) {
          void handle.remove();
          return;
        }
        remove = () => { void handle.remove(); };
      })
      .catch((error) => {
        console.warn('Failed to listen for appStateChange:', error);
      });

    return () => {
      cancelled = true;
      remove?.();
    };
  }, [refreshIfExpiringSoon]);
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  // peekToken() is the synchronous fast path so the web app doesn't paint a
  // logged-out frame; the hydrate effect below covers backends that can only
  // answer asynchronously.
  const [token, setToken] = useState<string | null>(peekToken());
  const [isLoading, setIsLoading] = useState(true);
  const [isHydrated, setIsHydrated] = useState(false);
  const authCheckAttemptedRef = useRef<string | null>(null);
  // Set once login or logout has written a token, so a late-resolving hydrate can't
  // resurrect the value it read at mount.
  const tokenWrittenRef = useRef(false);

  // Logout function defined here so it can be used in interceptors
  // Use useCallback to ensure stable reference
  const logout = useCallback(() => {
    // Deregister the device FIRST: DELETE /devices/{token} is authenticated, so
    // running it after the JWT is torn down 401s and strands a device row that
    // keeps delivering this user's notifications to whoever signs in next.
    // Fire-and-forget — a failed deregistration must not block signing out.
    void disablePush();

    setUser(null);
    setToken(null);
    tokenWrittenRef.current = true;
    void clearToken();
    delete axios.defaults.headers.common['Authorization'];
    authCheckAttemptedRef.current = null;
  }, []);

  // Store a freshly minted backend JWT everywhere it needs to live. Split out of
  // handleAuthSuccess because the silent-refresh paths want the storage half
  // without paying for another /auth/me round trip — clearing
  // authCheckAttemptedRef makes the checkAuth effect below revalidate anyway.
  const applyToken = useCallback(async (accessToken: string, expiresIn: number) => {
    const expirationTime = Date.now() + expiresIn * 1000;

    authCheckAttemptedRef.current = null;
    tokenWrittenRef.current = true;
    setToken(accessToken);

    // Set the header manually as well: setToken schedules a re-render, but the very
    // next request may be issued before that effect runs.
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

    await persistToken(accessToken, expirationTime);
  }, []);

  // Adopt the persisted token. With the localStorage backend this is a no-op that
  // confirms what peekToken() already returned; it exists so a Keychain-backed
  // native store (async-only reads) can hand us the session without the app
  // rendering — and redirecting — as logged out first.
  useEffect(() => {
    let cancelled = false;

    getToken()
      .then((stored) => {
        if (cancelled || tokenWrittenRef.current) return;
        setToken((current) => (stored !== current ? stored : current));
      })
      .catch((error) => {
        console.error('Failed to read persisted token:', error);
      })
      .finally(() => {
        if (!cancelled) setIsHydrated(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Ensure axios baseURL is set correctly (handles cases where module loads before window.location is ready)
  useEffect(() => {
    const correctBaseUrl = getApiBaseUrl();
    if (axios.defaults.baseURL !== correctBaseUrl) {
      console.log(`Updating axios baseURL from ${axios.defaults.baseURL} to ${correctBaseUrl}`);
      axios.defaults.baseURL = correctBaseUrl;
    }
  }, []);

  // Configure axios defaults and interceptors
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }

    // Add axios interceptor to handle 401 errors
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        // If we get a 401 error, the token is invalid or expired
        if (error.response?.status !== 401) {
          return Promise.reject(error);
        }

        // Try exactly one silent re-exchange before giving up. A 401 here usually
        // means the 7-day backend JWT lapsed while the Firebase session is still
        // perfectly healthy, so re-minting it costs one round trip and the user
        // never sees the login screen. _authRetry keeps a failed retry — or the
        // exchange request itself — from recursing.
        const config = error.config as RetryableConfig | undefined;
        if (config && !config._authRetry && canRefreshSilently()) {
          try {
            const exchange = await refreshBackendToken();
            if (exchange) {
              await applyToken(exchange.access_token, exchange.expires_in);
              return axios.request({
                ...config,
                _authRetry: true,
                headers: {
                  ...(config.headers as Record<string, unknown> | undefined),
                  Authorization: `Bearer ${exchange.access_token}`,
                },
              } as RetryableConfig);
            }
          } catch (refreshError) {
            console.warn('Silent token refresh after 401 failed:', refreshError);
          }
        }

        console.log('Received 401 error, logging out...');
        logout();
        requestLoginRedirect();
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [token, logout, applyToken]);

  // Check if user is logged in on app start
  useEffect(() => {
    // Wait for the token store to answer before deciding anyone is logged out —
    // otherwise an async (Keychain) backend would flash the login screen on launch.
    if (!isHydrated) return;

    const checkAuth = async () => {
      if (token) {
        // Prevent repeated auth check attempts for the same token
        // This prevents infinite loops if a token is invalid
        if (authCheckAttemptedRef.current === token) {
          // Silently skip - this is expected behavior to prevent infinite loops
          setIsLoading(false);
          return;
        }
        
        // Mark this token as attempted
        authCheckAttemptedRef.current = token;

        // Check if token is expired
        if (await isTokenExpired()) {
          // Only log in development to reduce console noise (StrictMode causes double logs)
          if (process.env.NODE_ENV === 'development') {
            console.log('Token expired, logging out...');
          }
          logout();
          setIsLoading(false);
          return;
        }

        try {
          const response = await axios.get('/auth/me');
          setUser(response.data);
        } catch (error) {
          console.error('Auth check failed:', error);
          logout();
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, [token, logout, isHydrated]);

  // Helper function to handle token storage and user retrieval
  const handleAuthSuccess = useCallback(async (access_token: string, expires_in: number) => {
    await applyToken(access_token, expires_in);

    // Get user info
    const userResponse = await axios.get('/auth/me');
    setUser(userResponse.data);
  }, [applyToken]);

  const loginWithFirebase = useCallback(async (firebaseToken: string) => {
    try {
      const response = await axios.post('/auth/firebase', null, {
        params: { firebase_token: firebaseToken }
      });
      const { access_token, expires_in } = response.data;
      await handleAuthSuccess(access_token, expires_in);
    } catch (error: any) {
      console.error('Firebase login failed:', error);
      // Re-throw with a more user-friendly message if it's an email verification error
      if (error?.response?.status === 403 && error?.response?.data?.detail) {
        const verificationError = new Error(error.response.data.detail);
        (verificationError as any).isEmailVerificationError = true;
        throw verificationError;
      }
      throw error;
    }
  }, [handleAuthSuccess]);

  useBackendTokenRefresh(token, applyToken);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    user,
    token,
    loginWithFirebase,
    logout,
    isLoading,
  }), [user, token, loginWithFirebase, logout, isLoading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
